import { AdaaHelper } from "../../../../../../../core/utils";
import { SharedFields, teamMembersSectionConfig, textField } from "../../configs";
import {
  AccordionSectionConfigType,
  ListSectionConfigType,
  ScreenSectionConfigType,
  ScreenType,
} from "../../wf-difference.types";
import { actualEndDateField, actualStartDateField } from "../tp-screen/milestones-config";

const contributingEntitiesField: ListSectionConfigType = {
  field: "contributingEntities",
  label: "kpi.contributing_entities",
  view: "list",
  idKey: "entityId",
  derivedList: (data: unknown) => {
    const list = data as { otherEntityId?: number }[];
    return list.filter(($i) => !AdaaHelper.isDefined($i?.otherEntityId));
  },
  format: ($o) => AdaaHelper.getItemValueByToken($o, "entityName"),
};

const otherContributingEntitiesField: ListSectionConfigType = {
  field: "contributingEntities",
  label: "other_entities.contrib_entities",
  view: "list",
  idKey: "otherEntityId",
  derivedList: (data: unknown) => {
    const list = data as { otherEntityId?: number }[];
    return list.filter(($i) => AdaaHelper.isDefined($i?.otherEntityId));
  },
  format: ($o) => AdaaHelper.getItemValueByToken($o, "otherEntityName"),
};

const targetAudienceField: ListSectionConfigType = {
  field: "targetAudience",
  label: "national_projects.target_audience",
  view: "list",
  idKey: "id",
  format: ($o) => AdaaHelper.getItemValueByToken($o, "audience"),
};

const expectedImpactField: ListSectionConfigType = {
  field: "expectedImpact",
  label: "national_projects.expected_impact",
  view: "list",
  idKey: "id",
  format: ($o) => AdaaHelper.getItemValueByToken($o, "impact"),
};

const commentsField: ListSectionConfigType = {
  field: "comments",
  label: "national_projects.comments",
  view: "list",
  idKey: "id",
  format: ($o) => AdaaHelper.getItemValueByToken($o, "comments"),
};

const allocatedBudgetField: ScreenSectionConfigType = {
  field: "budget",
  label: "initiatives.budget",
  view: "normal",
};

const weightField: ScreenSectionConfigType = {
  field: "workload",
  label: "nkpi.weight",
  view: "normal",
  /**
   * param has 2 types:
   * { workload: { workload: number } } This one is used in the parent object to color the accordion color
   * { workload: number } This one is used to color the cell of the workload
   */
  hasDiff: ($newValue: unknown, $oldValue: unknown) => {
    const $n = $newValue as { workload: { workload: number } } | { workload: number };
    const $o = $oldValue as { workload: { workload: number } } | { workload: number };
    if (typeof $o?.workload === "object" && typeof $n?.workload === "object")
      return $n?.workload?.workload !== $o?.workload?.workload;
    else return $n?.workload !== $o?.workload;
  },
  format: (data: { workload: { workload: number } }) => {
    if (data?.workload && typeof data.workload?.workload === "number") {
      return data.workload?.workload;
    }
    return "";
  },
};

const milestonesSectionConfig = [
  ...SharedFields.name,
  ...SharedFields.description,
  {
    ...SharedFields.startDate,
    label: "national_projects.planned_start_date",
  },
  {
    ...SharedFields.endDate,
    label: "national_projects.planned_end_date",
  },
  {
    ...SharedFields.entityIdField,
    format: (value: Record<string, string>) => AdaaHelper.getItemValueByToken(value, "entityName"),
    label: "national_projects.accountable_entity",
  },
  {
    ...SharedFields.entityIdField,
    format: (value: Record<string, string>) => AdaaHelper.getItemValueByToken(value, "otherEntityName"),
    label: "national_projects.other_accountable_entity",
  },
  SharedFields.ownerField,
  allocatedBudgetField,
  weightField,
  SharedFields.nationalSectorsField,
];

const projectMilestonesField: AccordionSectionConfigType = {
  view: "accordion",
  field: "milestones",
  title: ($o) => AdaaHelper.getItemValueByToken($o, "name"),
  idKey: "id",
  config: milestonesSectionConfig,
  label: "national_projects.national_projects_milestone.title",
  hasDiff: ($newValue: unknown, $oldValue: unknown) => {
    const $n = $newValue as Record<string, unknown>;
    const $o = $oldValue as Record<string, unknown>;
    const keys = [
      "id",
      "nameAE",
      "nameEN",
      "dscAE",
      "dscAE",
      "startDate",
      "endDate",
      "entityId",
      "otherEntityId",
      "owner",
      "budget",
      "milestoneOrder",
    ];

    return keys.some((key) => $o[key] !== $n[key]);
  },
};

const projectTeamMembersField: AccordionSectionConfigType = {
  view: "accordion",
  field: "projectTeamMembers",
  title: ($o) => AdaaHelper.getItemValueByToken($o, "name"),
  idKey: "id",
  config: teamMembersSectionConfig,
  label: "national_projects.project_team_members.popup_title",
};

const countryField: ScreenSectionConfigType = {
  field: "countryId",
  view: "normal",
  label: "international_projects.countries.single",
  format: (value: Record<string, string>) => AdaaHelper.getItemValueByToken(value, "countryName"),
};

const sectorField: ScreenSectionConfigType = {
  field: "sectorId",
  view: "normal",
  label: "international_projects.countries.sector",
  format: (value: Record<string, string>) => AdaaHelper.getItemValueByToken(value, "sectorName"),
};

const countOfBeneficiariesField: ScreenSectionConfigType = {
  field: "countOfBeneficiaries",
  label: "international_projects.projects.count_of_beneficiaries",
  view: "normal",
};

const currencyField: ScreenSectionConfigType = {
  field: "currencyId",
  label: "international_projects.projects.currency",
  view: "normal",
  format: (value: Record<string, string>) => AdaaHelper.getItemValueByToken(value, "currencyName"),
};

const projectField = () => {
  const field = textField("internationalProjectName", { use: "current" }) as ScreenSectionConfigType;
  field.label = "international_projects.projects.single";
  return field;
};

const expensesField: ScreenSectionConfigType = {
  field: "expense",
  label: "initiatives.expenses",
  view: "normal",
};

const progressField: ScreenSectionConfigType = {
  field: "progress",
  label: "box_status.activity_progress",
  view: "normal",
};

const signOffField: ScreenSectionConfigType = {
  field: "signOff",
  label: "national_projects.sign_off.justification",
  view: "normal",
};

const signOffAttachmentField: ScreenSectionConfigType = {
  field: "signOffAttachment",
  label: "national_projects.sign_off.evidence_wf_cs",
  view: "normal",
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  format: ($o) => ($o as any).signOffAttachment?.filename,
  hasDiff: ($newValue: unknown, $oldValue: unknown) => {
    const $n = $newValue as { id: number };
    const $o = $oldValue as { id: number };
    return $n?.id !== $o?.id;
  },
  isClickable: true,
};

export const intpProjectScreenConfig = {
  type: "kpi" as ScreenType,
  sections: [
    ...SharedFields.name,
    ...SharedFields.description,
    countryField,
    sectorField,
    {
      ...SharedFields.startDate,
      label: "national_projects.planned_start_date",
    },
    {
      ...SharedFields.endDate,
      label: "national_projects.planned_end_date",
    },
    {
      ...SharedFields.entityIdField,
      label: "national_projects.accountable_entity",
      format: (value: Record<string, string>) => AdaaHelper.getItemValueByToken(value, "accountableEntityName"),
    },
    {
      ...SharedFields.ownerField,
      label: "national_projects.entity_lead",
    },
    {
      ...SharedFields.ownerField,
      label: "national_projects.project_coordinator",
      field: "projectCoordinator",
      format: (value: Record<string, string>) => AdaaHelper.getItemValueByToken(value, "projectCoordinatorName"),
    },
    countOfBeneficiariesField,
    currencyField,
    contributingEntitiesField,
    otherContributingEntitiesField,
    SharedFields.nationalSectorsField,
    targetAudienceField,
    expectedImpactField,
    commentsField,
    projectMilestonesField,
    projectTeamMembersField,
    SharedFields.attachmentsField,
  ],
};

export const intpMilestoneScreenConfig = {
  type: "kpi" as ScreenType,
  sections: [
    projectField(),
    ...SharedFields.name,
    ...SharedFields.description,
    {
      ...SharedFields.startDate,
      label: "national_projects.planned_start_date",
    },
    {
      ...SharedFields.endDate,
      label: "national_projects.planned_end_date",
    },
    actualStartDateField,
    actualEndDateField,
    {
      ...SharedFields.entityIdField,
      label: "national_projects.accountable_entity",
    },
    SharedFields.ownerField,
    allocatedBudgetField,
    expensesField,
    progressField,
    signOffField,
    signOffAttachmentField,
    SharedFields.nationalSectorsField,
    commentsField,
    SharedFields.attachmentsField,
  ],
};
